import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import FileUploader from './FileUploader'

function BookingsManager() {
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentBooking, setCurrentBooking] = useState({
    title: '',
    description: '',
    path: '',
    image_url: '',
    cta_text: 'Book Now',
    cta_color: 'blue',
    active: true,
    display_order: 0
  })
  const [isEditing, setIsEditing] = useState(false)
  
  useEffect(() => {
    fetchBookings()
  }, [])
  
  const fetchBookings = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('codevaro_bookings_j5k7l9m2n3')
        .select('*')
        .order('display_order', { ascending: true })
      
      if (error) throw error
      
      setBookings(data || [])
    } catch (error) {
      console.error('Error fetching bookings:', error)
      setError('Σφάλμα κατά τη φόρτωση των ραντεβού')
    } finally {
      setLoading(false)
    }
  }
  
  const openAddModal = () => {
    // Find the highest display order and add 1
    const maxOrder = bookings.reduce((max, booking) => 
      booking.display_order > max ? booking.display_order : max, 0)
    
    setCurrentBooking({
      title: '',
      description: '',
      path: '',
      image_url: '',
      cta_text: 'Book Now',
      cta_color: 'blue',
      active: true,
      display_order: maxOrder + 1
    })
    setIsEditing(false)
    setIsModalOpen(true)
  }
  
  const openEditModal = (booking) => {
    setCurrentBooking({...booking})
    setIsEditing(true)
    setIsModalOpen(true)
  }
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setCurrentBooking({
      ...currentBooking,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleFileUploaded = (url) => {
    console.log("File uploaded, setting URL:", url)
    setCurrentBooking({
      ...currentBooking,
      image_url: url
    })
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      setError(null)
      
      // Validate required fields
      if (!currentBooking.title?.trim()) {
        throw new Error('Ο τίτλος του ραντεβού είναι υποχρεωτικός')
      }
      
      if (!currentBooking.path?.trim()) {
        throw new Error('Το TidyCal path είναι υποχρεωτικό')
      }
      
      // Format the path correctly
      let formattedPath = currentBooking.path.trim()
      // If the path is a full URL, extract just the path part
      if (formattedPath.includes('tidycal.com/')) {
        formattedPath = formattedPath.split('tidycal.com/')[1]
      }
      // Remove any leading or trailing slashes
      formattedPath = formattedPath.replace(/^\/|\/$/g, '')
      
      // Prepare the booking data
      const bookingData = {
        title: currentBooking.title.trim(),
        description: currentBooking.description?.trim() || null,
        path: formattedPath,
        image_url: currentBooking.image_url || null,
        cta_text: currentBooking.cta_text?.trim() || 'Book Now',
        cta_color: currentBooking.cta_color || 'blue',
        active: !!currentBooking.active,
        display_order: parseInt(currentBooking.display_order) || 0
      }
      
      console.log('Submitting booking data:', bookingData)
      
      if (isEditing) {
        // Update existing booking
        const { data, error } = await supabase
          .from('codevaro_bookings_j5k7l9m2n3')
          .update(bookingData)
          .eq('id', currentBooking.id)
          .select()
        
        if (error) throw error
        
        setSuccess('Το ραντεβού ενημερώθηκε επιτυχώς')
      } else {
        // Add new booking
        const { data, error } = await supabase
          .from('codevaro_bookings_j5k7l9m2n3')
          .insert([bookingData])
          .select()
        
        if (error) throw error
        
        setSuccess('Το ραντεβού δημιουργήθηκε επιτυχώς')
      }
      
      setIsModalOpen(false)
      fetchBookings()
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 5000)
    } catch (error) {
      console.error('Error saving booking:', error)
      setError('Σφάλμα κατά την αποθήκευση του ραντεβού: ' + (error.message || error))
    } finally {
      setLoading(false)
    }
  }
  
  const toggleBookingStatus = async (id, currentStatus) => {
    try {
      setLoading(true)
      
      const { error } = await supabase
        .from('codevaro_bookings_j5k7l9m2n3')
        .update({ active: !currentStatus })
        .eq('id', id)
      
      if (error) throw error
      
      fetchBookings()
      setSuccess(`Η κατάσταση του ραντεβού ενημερώθηκε επιτυχώς`)
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 5000)
    } catch (error) {
      console.error('Error updating booking status:', error)
      setError('Σφάλμα κατά την ενημέρωση της κατάστασης του ραντεβού')
    } finally {
      setLoading(false)
    }
  }
  
  const deleteBooking = async (id) => {
    if (!confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το ραντεβού;')) {
      return
    }
    
    try {
      setLoading(true)
      
      const { error } = await supabase
        .from('codevaro_bookings_j5k7l9m2n3')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      fetchBookings()
      setSuccess('Το ραντεβού διαγράφηκε επιτυχώς')
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 5000)
    } catch (error) {
      console.error('Error deleting booking:', error)
      setError('Σφάλμα κατά τη διαγραφή του ραντεβού')
    } finally {
      setLoading(false)
    }
  }
  
  const moveBooking = async (id, direction) => {
    try {
      // Find the current booking
      const currentIndex = bookings.findIndex(booking => booking.id === id)
      if (currentIndex === -1) return
      
      // Calculate new index based on direction
      const newIndex = direction === 'up' 
        ? Math.max(0, currentIndex - 1)
        : Math.min(bookings.length - 1, currentIndex + 1)
      
      // If no change in position, exit
      if (newIndex === currentIndex) return
      
      const currentBooking = bookings[currentIndex]
      const targetBooking = bookings[newIndex]
      
      // Swap display orders
      const { error: error1 } = await supabase
        .from('codevaro_bookings_j5k7l9m2n3')
        .update({ display_order: targetBooking.display_order })
        .eq('id', currentBooking.id)
      
      if (error1) throw error1
      
      const { error: error2 } = await supabase
        .from('codevaro_bookings_j5k7l9m2n3')
        .update({ display_order: currentBooking.display_order })
        .eq('id', targetBooking.id)
      
      if (error2) throw error2
      
      fetchBookings()
    } catch (error) {
      console.error('Error reordering bookings:', error)
      setError('Σφάλμα κατά την αλλαγή σειράς των ραντεβού')
    }
  }
  
  const reorderBookings = async () => {
    try {
      setLoading(true)
      
      // Update all bookings with sequential display_order
      for (let i = 0; i < bookings.length; i++) {
        const { error } = await supabase
          .from('codevaro_bookings_j5k7l9m2n3')
          .update({ display_order: i + 1 })
          .eq('id', bookings[i].id)
        
        if (error) throw error
      }
      
      fetchBookings()
      setSuccess('Η σειρά των ραντεβού ενημερώθηκε επιτυχώς')
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 5000)
    } catch (error) {
      console.error('Error reordering bookings:', error)
      setError('Σφάλμα κατά την αναδιάταξη των ραντεβού')
    } finally {
      setLoading(false)
    }
  }
  
  const testTidyCalLink = (path) => {
    let formattedPath = path.trim()
    // If the path is a full URL, extract just the path part
    if (formattedPath.includes('tidycal.com/')) {
      formattedPath = formattedPath.split('tidycal.com/')[1]
    }
    // Remove any leading or trailing slashes
    formattedPath = formattedPath.replace(/^\/|\/$/g, '')
    
    window.open(`https://tidycal.com/${formattedPath}`, '_blank')
  }
  
  return (
    <div>
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Διαχείριση Ραντεβού</h1>
        <div className="flex space-x-2">
          <button
            onClick={reorderBookings}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <SafeIcon icon={FiIcons.FiList} className="mr-2" />
            Επαναδιάταξη
          </button>
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2" />
            Νέο Ραντεβού
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
          <button
            className="absolute top-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <SafeIcon icon={FiIcons.FiX} className="h-4 w-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{success}</span>
          <button
            className="absolute top-0 right-0 px-4 py-3"
            onClick={() => setSuccess(null)}
          >
            <SafeIcon icon={FiIcons.FiX} className="h-4 w-4" />
          </button>
        </div>
      )}
      
      <div className="mt-6">
        {loading && !isModalOpen && (
          <div className="flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        )}
        
        {!loading && bookings.length === 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
            <SafeIcon icon={FiIcons.FiCalendar} className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Δεν υπάρχουν ραντεβού</h3>
            <p className="mt-1 text-sm text-gray-500">
              Ξεκινήστε προσθέτοντας ένα νέο ραντεβού με το TidyCal.
            </p>
          </div>
        )}
        
        {!loading && bookings.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {bookings.map((booking, index) => (
                <li key={booking.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 font-medium text-sm mr-3">
                          {booking.display_order || index + 1}
                        </span>
                        <div className="flex items-center">
                          {booking.image_url && (
                            <div className="mr-4 flex-shrink-0 h-16 w-16 rounded overflow-hidden">
                              <img 
                                src={booking.image_url} 
                                alt={booking.title} 
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                                }}
                              />
                            </div>
                          )}
                          <div>
                            <h3 className="text-lg font-medium text-blue-600">{booking.title}</h3>
                            {booking.description && (
                              <p className="mt-1 text-sm text-gray-600 max-w-2xl">
                                {booking.description}
                              </p>
                            )}
                            {booking.cta_text && (
                              <span className={`mt-2 inline-block px-3 py-1 text-xs font-medium rounded-full bg-${booking.cta_color || 'blue'}-100 text-${booking.cta_color || 'blue'}-800`}>
                                CTA: {booking.cta_text}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          booking.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {booking.active ? 'Ενεργό' : 'Ανενεργό'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <div className="flex items-center text-sm text-gray-500">
                          <SafeIcon icon={FiIcons.FiLink} className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          <span>{booking.path}</span>
                          <button
                            onClick={() => testTidyCalLink(booking.path)}
                            className="ml-2 text-blue-500 hover:text-blue-700"
                            title="Test TidyCal link"
                          >
                            <SafeIcon icon={FiIcons.FiExternalLink} className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center sm:mt-0">
                        <div className="flex space-x-1">
                          {/* Reordering buttons */}
                          <button
                            onClick={() => moveBooking(booking.id, 'up')}
                            disabled={index === 0}
                            className={`inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 ${
                              index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                            title="Move up"
                          >
                            <SafeIcon icon={FiIcons.FiArrowUp} className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => moveBooking(booking.id, 'down')}
                            disabled={index === bookings.length - 1}
                            className={`inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 ${
                              index === bookings.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                            title="Move down"
                          >
                            <SafeIcon icon={FiIcons.FiArrowDown} className="h-4 w-4" />
                          </button>
                          
                          {/* Toggle active state */}
                          <button
                            onClick={() => toggleBookingStatus(booking.id, booking.active)}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                              booking.active 
                                ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200' 
                                : 'text-green-700 bg-green-100 hover:bg-green-200'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                          >
                            <SafeIcon 
                              icon={booking.active ? FiIcons.FiEyeOff : FiIcons.FiEye} 
                              className="mr-1" 
                            />
                            {booking.active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                          </button>
                          
                          {/* Edit button */}
                          <button
                            onClick={() => openEditModal(booking)}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <SafeIcon icon={FiIcons.FiEdit2} className="mr-1" />
                            Επεξεργασία
                          </button>
                          
                          {/* Delete button */}
                          <button
                            onClick={() => deleteBooking(booking.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <SafeIcon icon={FiIcons.FiTrash2} className="mr-1" />
                            Διαγραφή
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Modal for adding/editing bookings */}
      {isModalOpen && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {isEditing ? 'Επεξεργασία Ραντεβού' : 'Νέο Ραντεβού'}
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                            Τίτλος *
                          </label>
                          <input
                            type="text"
                            name="title"
                            id="title"
                            required
                            value={currentBooking.title || ''}
                            onChange={handleInputChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Περιγραφή
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            rows="3"
                            value={currentBooking.description || ''}
                            onChange={handleInputChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Προαιρετική περιγραφή του τύπου ραντεβού"
                          ></textarea>
                        </div>

                        <div>
                          <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                            Εικόνα
                          </label>
                          {currentBooking.image_url && (
                            <div className="mt-2 mb-4">
                              <img 
                                src={currentBooking.image_url} 
                                alt="Booking preview" 
                                className="h-32 w-auto object-cover rounded-md"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => setCurrentBooking({...currentBooking, image_url: ''})}
                                className="mt-2 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                              >
                                <SafeIcon icon={FiIcons.FiTrash2} className="mr-1" />
                                Αφαίρεση εικόνας
                              </button>
                            </div>
                          )}
                          <FileUploader 
                            onFileUploaded={handleFileUploaded}
                            acceptedFileTypes="image/*"
                            maxSizeMB={5}
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="path" className="block text-sm font-medium text-gray-700">
                            TidyCal Path *
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                              tidycal.com/
                            </span>
                            <input
                              type="text"
                              name="path"
                              id="path"
                              required
                              value={currentBooking.path || ''}
                              onChange={handleInputChange}
                              className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300"
                              placeholder="username/booking-page"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Εισάγετε το path από το TidyCal (π.χ. "username/initial-consultation")
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="cta_text" className="block text-sm font-medium text-gray-700">
                              Κείμενο CTA
                            </label>
                            <input
                              type="text"
                              name="cta_text"
                              id="cta_text"
                              value={currentBooking.cta_text || ''}
                              onChange={handleInputChange}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              placeholder="Book Now"
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="cta_color" className="block text-sm font-medium text-gray-700">
                              Χρώμα CTA
                            </label>
                            <select
                              id="cta_color"
                              name="cta_color"
                              value={currentBooking.cta_color || 'blue'}
                              onChange={handleInputChange}
                              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            >
                              <option value="blue">Μπλε</option>
                              <option value="green">Πράσινο</option>
                              <option value="orange">Πορτοκαλί</option>
                              <option value="red">Κόκκινο</option>
                              <option value="purple">Μωβ</option>
                              <option value="indigo">Indigo</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            id="active"
                            name="active"
                            type="checkbox"
                            checked={currentBooking.active || false}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                            Ενεργό
                          </label>
                        </div>
                        
                        <div>
                          <label htmlFor="display_order" className="block text-sm font-medium text-gray-700">
                            Σειρά εμφάνισης
                          </label>
                          <input
                            type="number"
                            name="display_order"
                            id="display_order"
                            min="0"
                            value={currentBooking.display_order || 0}
                            onChange={handleInputChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Η σειρά με την οποία θα εμφανίζεται το ραντεβού (μικρότερος αριθμός = πρώτο)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {loading ? (
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2" />
                    ) : (
                      <SafeIcon icon={isEditing ? FiIcons.FiSave : FiIcons.FiPlus} className="mr-2" />
                    )}
                    {isEditing ? 'Αποθήκευση' : 'Προσθήκη'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingsManager